import React, { useCallback, useEffect, useState } from 'react';
import api from '../../utils/api';
import {
  Button,
  Card,
  CardHeader,
  EmptyState,
  Field,
  Input,
  Modal,
  Select,
  StatusPill,
  TableSkeleton,
  TableWrap,
  Td,
  Th,
  Tr
} from '../../components/admin/ui';
import { useConfirm, useToast } from '../../components/admin/overlays';
import { IconAlert, IconBan, IconCheck, IconPlus, IconRefresh, IconUser } from '../../components/icons';
import { formatDateTime } from '../../components/admin/labels';
import { merchantRoleLabel, userStatusTone } from '../../components/plans';
import { fieldErrorsFrom } from '../../utils/formErrors';
import { useMerchantSession } from '../../components/merchant/MerchantLayout';
import { UpgradeModal } from '../../components/merchant/PlanUpgradeGrid';

const emptyForm = { name: '', email: '', merchantRole: 'agent', password: '' };

const MerchantTeam = () => {
  const session = useMerchantSession();
  const confirm = useConfirm();
  const toast = useToast();

  const [users, setUsers] = useState([]);
  const [seats, setSeats] = useState({ allowed: 0, used: 0, included: 0, additional: 0 });
  // Seat pricing and whether this merchant can buy one at all — the plan is
  // sold per user, so "add a user" is a billing decision, not just an invite.
  const [billing, setBilling] = useState(null);
  const [merchant, setMerchant] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [inviteLink, setInviteLink] = useState(null);

  const isAdmin = session?.merchantRole === 'admin';
  const seatPrice = Number(billing?.seatPrice) || 0;
  // The plan includes the owner's seat; everyone after that is a paid seat on
  // the same subscription, which the owner buys themselves from here.
  const seatsFull = seats.used >= Math.max(1, seats.allowed);
  const canBuySeats = Boolean(billing?.canBuySeats);
  // Seats can be unbuyable for two different reasons, and only one of them is
  // the merchant's to fix: no subscription to put a seat on (choose a plan),
  // versus Stripe not being set up on this deployment at all (nothing they
  // can do about it, so don't send them to a plan chooser that won't help).
  const seatsBlockedBySetup = !canBuySeats && billing?.code === 'STRIPE_NOT_CONFIGURED';
  const needsSubscription = seatsFull && !canBuySeats && !seatsBlockedBySetup;
  const needsPaidSeat = seatsFull && canBuySeats;

  const fetchTeam = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/portal/team');
      setUsers(data.users || []);
      setSeats(data.seats || { allowed: 0, used: 0, included: 0, additional: 0 });
      setBilling(data.billing || null);
    } catch (error) {
      toast('Could not load your team.', { tone: 'error' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  // Only for the plan chooser below — the team endpoint carries the seat
  // pricing this page reads, but PlanUpgradeGrid wants the whole merchant.
  useEffect(() => {
    let cancelled = false;
    api
      .get('/api/portal/me')
      .then(({ data }) => {
        if (!cancelled) setMerchant(data.merchant);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const handleAdd = async () => {
    const found = {};
    if (!form.name.trim()) found.name = 'Enter their name.';
    if (!form.email.trim()) found.email = 'Enter an email address.';
    else if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) found.email = 'Enter a valid email address.';
    if (!form.password) found.password = 'Set a password for them.';
    else if (form.password.length < 8) found.password = 'Use at least 8 characters.';
    if (Object.keys(found).length) {
      setFormErrors(found);
      return;
    }
    setFormErrors({});

    setSaving(true);
    try {
      await api.post('/api/portal/team', {
        name: form.name.trim(),
        email: form.email.trim(),
        merchantRole: form.merchantRole,
        password: form.password,
        // The modal showed what this costs before the button was pressed, so
        // this says "yes, add the paid seat too" — the server refuses to buy
        // one without it.
        ...(needsPaidSeat && { buySeat: true })
      });
      setShowAdd(false);
      setForm(emptyForm);
      await fetchTeam();
      toast(
        needsPaidSeat
          ? `User added — a seat was added to your subscription at £${seatPrice.toFixed(2)}/month.`
          : 'User added.',
        { tone: 'success' }
      );
    } catch (error) {
      setFormErrors(fieldErrorsFrom(error, { fallback: 'Could not add this user.' }));
    } finally {
      setSaving(false);
    }
  };

  const handleStatus = async (user, status) => {
    try {
      await api.patch(`/api/portal/team/${user._id}`, { status });
      await fetchTeam();
      toast(`${user.name} ${status === 'disabled' ? 'disabled' : 'enabled'}.`, { tone: 'success' });
    } catch (error) {
      toast(error.response?.data?.error || 'Could not update this user.', { tone: 'error' });
    }
  };

  const handleRole = async (user, merchantRole) => {
    try {
      await api.patch(`/api/portal/team/${user._id}`, { merchantRole });
      await fetchTeam();
      toast(`${user.name} is now ${merchantRoleLabel(merchantRole).toLowerCase()}.`, { tone: 'success' });
    } catch (error) {
      toast(error.response?.data?.error || 'Could not change this role.', { tone: 'error' });
    }
  };

  const handleResendInvite = async (user) => {
    try {
      const { data } = await api.post(`/api/portal/team/${user._id}/invite`);
      if (data.emailed) {
        toast(`Invitation sent to ${user.email}.`, { tone: 'success' });
      } else {
        // Email delivery is not configured — hand the link over directly so the
        // invitation is still usable.
        setInviteLink({ user, link: data.link, expiryHours: data.expiryHours });
      }
      await fetchTeam();
    } catch (error) {
      toast(error.response?.data?.error || 'Could not send this invitation.', { tone: 'error' });
    }
  };

  const handleRemove = async (user) => {
    const ok = await confirm({
      title: 'Remove user',
      message: `Remove ${user.name} from your team?`,
      detail: 'Their paid seat is released from your subscription. This cannot be undone.',
      tone: 'danger',
      confirmLabel: 'Remove user'
    });
    if (!ok) return;

    try {
      await api.delete(`/api/portal/team/${user._id}`);
      await fetchTeam();
      toast('User removed.', { tone: 'success' });
    } catch (error) {
      toast(error.response?.data?.error || 'Could not remove this user.', { tone: 'error' });
    }
  };

  return (
    <Card>
      <CardHeader
        title={`Users (${seats.used}/${Math.max(1, seats.allowed)} seats used)`}
        description={
          isAdmin
            ? `Admins manage the team and billing. Agents work cases.${
                seatPrice > 0 ? ` Each extra user is £${seatPrice.toFixed(2)} per month.` : ''
              }`
            : 'Only a team admin can add or change users.'
        }
        actions={
          <>
            <Button variant="ghost" size="sm" onClick={fetchTeam}>
              <IconRefresh size={15} />
              Refresh
            </Button>
            {isAdmin && (
              <Button
                size="sm"
                onClick={() => (needsSubscription ? setShowUpgrade(true) : setShowAdd(true))}
              >
                <IconPlus size={15} />
                Add user
              </Button>
            )}
          </>
        }
      />

      {/* Seats are the product, so a full one isn't a dead end any more: the
          owner either buys the next seat here, or picks a plan to have one to
          buy at all. */}
      {needsSubscription && isAdmin && (
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-ink-200 bg-warning-50 px-5 py-3.5">
          <div className="flex items-start gap-3">
            <IconAlert size={17} className="mt-0.5 shrink-0 text-warning-600" />
            <p className="text-sm text-warning-700">
              <span className="font-600">Adding teammates needs a subscription.</span> ParcelBuddy
              is priced per user
              {seatPrice > 0 ? ` — £${seatPrice.toFixed(2)} each per month` : ''}, so choose a plan
              to start building your team.
            </p>
          </div>
          <Button variant="secondary" size="sm" onClick={() => setShowUpgrade(true)}>
            Choose a plan
          </Button>
        </div>
      )}

      {seatsFull && seatsBlockedBySetup && isAdmin && (
        <div className="flex items-start gap-3 border-b border-ink-200 bg-warning-50 px-5 py-3.5">
          <IconAlert size={17} className="mt-0.5 shrink-0 text-warning-600" />
          <p className="text-sm text-warning-700">
            Every seat is in use, and seats can&apos;t be billed on this environment right now.
            Disable a user to free a seat, or contact ParcelBuddy.
          </p>
        </div>
      )}

      {needsPaidSeat && isAdmin && (
        <div className="flex items-start gap-3 border-b border-ink-200 bg-ink-50 px-5 py-3.5">
          <IconUser size={17} className="mt-0.5 shrink-0 text-ink-500" />
          <p className="text-sm text-ink-700">
            Every seat you have is in use. Adding another user adds a seat to your subscription
            {seatPrice > 0 ? ` at £${seatPrice.toFixed(2)} per month` : ''}; removing a user
            releases theirs again.
          </p>
        </div>
      )}

      {loading ? (
        <TableSkeleton rows={4} cols={5} />
      ) : users.length === 0 ? (
        <EmptyState
          icon={IconUser}
          title="No users yet"
          description="Add your support team so they can work cases with you."
        />
      ) : (
        <TableWrap>
          <thead>
            <tr>
              <Th>Name</Th>
              <Th>Email</Th>
              <Th>Role</Th>
              <Th>Status</Th>
              <Th>Last login</Th>
              {isAdmin && <Th className="text-right">Actions</Th>}
            </tr>
          </thead>
          <tbody>
            {users.map((user) => {
              const isSelf = String(user._id) === String(session?.id);
              return (
                <Tr key={user._id}>
                  <Td className="font-600 text-ink-900">
                    {user.name}
                    {isSelf && <span className="ml-2 text-xs font-400 text-ink-500">(you)</span>}
                  </Td>
                  <Td className="text-ink-600">{user.email}</Td>
                  <Td>
                    {isAdmin && !isSelf ? (
                      <Select
                        value={user.merchantRole || 'agent'}
                        onChange={(e) => handleRole(user, e.target.value)}
                        aria-label={`Role for ${user.name}`}
                        className="h-9 w-[8.5rem] text-[13px]"
                      >
                        <option value="agent">Agent</option>
                        <option value="admin">Admin</option>
                      </Select>
                    ) : (
                      merchantRoleLabel(user.merchantRole)
                    )}
                  </Td>
                  <Td>
                    <StatusPill tone={userStatusTone(user.status)}>{user.status}</StatusPill>
                  </Td>
                  <Td className="text-ink-500">
                    {user.lastLogin ? formatDateTime(user.lastLogin) : 'Never'}
                  </Td>
                  {isAdmin && (
                    <Td className="text-right">
                      {isSelf ? (
                        <span className="text-xs text-ink-500">—</span>
                      ) : (
                        <div className="flex justify-end gap-1.5">
                          {user.status !== 'disabled' && (
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => handleResendInvite(user)}
                            >
                              {user.status === 'invited' ? 'Resend invite' : 'Send reset'}
                            </Button>
                          )}
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() =>
                              handleStatus(user, user.status === 'disabled' ? 'active' : 'disabled')
                            }
                          >
                            {user.status === 'disabled' ? <IconCheck size={15} /> : <IconBan size={15} />}
                            {user.status === 'disabled' ? 'Enable' : 'Disable'}
                          </Button>
                          <Button variant="dangerGhost" size="sm" onClick={() => handleRemove(user)}>
                            Remove
                          </Button>
                        </div>
                      )}
                    </Td>
                  )}
                </Tr>
              );
            })}
          </tbody>
        </TableWrap>
      )}

      <Modal
        open={Boolean(inviteLink)}
        onClose={() => setInviteLink(null)}
        title="Invitation link"
        description={
          inviteLink
            ? `Email delivery is not configured, so send this link to ${inviteLink.user.email} yourself.`
            : ''
        }
        size="sm"
        footer={
          <Button variant="secondary" onClick={() => setInviteLink(null)}>
            Done
          </Button>
        }
      >
        <Input
          readOnly
          value={inviteLink?.link || ''}
          onClick={(e) => e.target.select()}
          className="font-mono text-xs"
        />
        <p className="mt-2 text-xs text-ink-500">
          Valid for {inviteLink?.expiryHours} hours, and can be used once.
        </p>
      </Modal>

      <Modal
        open={showAdd}
        onClose={() => {
          setShowAdd(false);
          setFormErrors({});
        }}
        title="Add user"
        description={`${seats.used} of ${Math.max(1, seats.allowed)} seats used.`}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowAdd(false)}>
              Cancel
            </Button>
            <Button onClick={handleAdd} loading={saving} variant={needsPaidSeat ? 'gold' : 'primary'}>
              {needsPaidSeat && seatPrice > 0
                ? `Add user — £${seatPrice.toFixed(2)}/mo`
                : 'Add user'}
            </Button>
          </>
        }
      >
        <div className="space-y-5">
          {/* Said before the button is pressed, not after: this user is what
              makes the subscription go up. */}
          {needsPaidSeat && (
            <div className="flex items-start gap-3 rounded-xl bg-gold-50/60 px-4 py-3.5 ring-1 ring-inset ring-gold-500/30">
              <IconUser size={17} className="mt-0.5 shrink-0 text-gold-700" />
              <p className="text-sm text-ink-700">
                You have no free seats, so adding this user adds a paid seat to your subscription
                {seatPrice > 0 ? (
                  <>
                    {' '}
                    — <span className="font-600 text-ink-900">£{seatPrice.toFixed(2)} per month</span>,
                    prorated from today.
                  </>
                ) : (
                  '.'
                )}
              </p>
            </div>
          )}

          {formErrors._general && (
            <div
              role="alert"
              className="rounded-lg bg-danger-50 px-3.5 py-3 text-sm text-danger-700 ring-1 ring-inset ring-danger-100"
            >
              {formErrors._general}
            </div>
          )}

          <Field label="Name" htmlFor="t-name" required error={formErrors.name}>
            <Input
              id="t-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              autoFocus
            />
          </Field>

          <Field label="Email" htmlFor="t-email" required error={formErrors.email}>
            <Input
              id="t-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </Field>

          <Field label="Role" htmlFor="t-role">
            <Select
              id="t-role"
              value={form.merchantRole}
              onChange={(e) => setForm({ ...form, merchantRole: e.target.value })}
            >
              <option value="agent">Agent — works cases</option>
              <option value="admin">Admin — also manages the team</option>
            </Select>
          </Field>

          <Field
            label="Password"
            htmlFor="t-pass"
            required
            error={formErrors.password}
            hint="Share it with them — they can change it once they sign in."
          >
            <Input
              id="t-pass"
              type="text"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="Minimum 8 characters"
            />
          </Field>
        </div>
      </Modal>

      {/* Same plan chooser the dashboard and cases pages open — a merchant
          with no subscription has nothing to put a seat on, so the way to a
          team starts here. */}
      <UpgradeModal
        open={showUpgrade}
        onClose={() => {
          setShowUpgrade(false);
          fetchTeam();
        }}
        merchant={merchant}
      />
    </Card>
  );
};

export default MerchantTeam;
